import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Gauge, ArrowRight, ArrowLeft, CheckCircle2, Sparkles, Loader2, Mail, Compass } from "lucide-react";

const LEVEL_STYLES = {
  "Very High Opportunity": "bg-amber-500 text-navy",
  "High Opportunity": "bg-gold text-navy",
  "Strong Opportunity": "bg-blue-100 text-blue-800",
  "Moderate Opportunity": "bg-slate-100 text-slate-700",
  "Well Positioned": "bg-emerald-100 text-emerald-800",
};

function track(event, extra = {}) {
  try { window.dispatchEvent(new CustomEvent("assessment_analytics", { detail: { event, ...extra } })); } catch {}
}

export default function Assessment({ onScheduleDemo, onExplore }) {
  const [config, setConfig] = useState(null);
  const [stepIdx, setStepIdx] = useState(-1); // -1 intro, 0..3 steps, 4 loading, 5 results
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [leadOpen, setLeadOpen] = useState(false);
  const [lead, setLead] = useState({ first_name: "", last_name: "", dealership_name: "", email: "", phone: "" });
  const [leadDone, setLeadDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/public/assessment-config").then(({ data }) => setConfig(data)).catch(() => {}); }, []);

  if (!config) return <section id="assessment" className="py-20 bg-secondary/40" data-testid="assessment-section" />;

  const steps = config.steps;
  const qForStep = (s) => config.questions.filter((q) => q.step === s + 1 && (!q.show_if || Object.entries(q.show_if).every(([k, v]) => answers[k] === v)));

  const setSingle = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));
  const toggleMulti = (qid, val, max) => setAnswers((a) => {
    const cur = a[qid] || [];
    if (cur.includes(val)) return { ...a, [qid]: cur.filter((x) => x !== val) };
    if (cur.length >= max) { toast.info(`Choose up to ${max}`); return a; }
    return { ...a, [qid]: [...cur, val] };
  });

  const stepComplete = (s) => qForStep(s).every((q) => q.optional || (q.type === "multi" ? (answers[q.id] || []).length > 0 : !!answers[q.id]));

  const start = () => { track("Assessment Started"); setStepIdx(0); };
  const next = () => {
    if (!stepComplete(stepIdx)) return toast.error("Please answer the questions to continue");
    track("Assessment Step Completed", { step: stepIdx + 1 });
    if (stepIdx < 3) setStepIdx(stepIdx + 1); else submit();
  };
  const back = () => setStepIdx((i) => Math.max(0, i - 1));

  const submit = async () => {
    setStepIdx(4);
    track("Assessment Completed");
    try {
      const { data } = await api.post("/public/assessment", { answers });
      setResult(data);
      setStepIdx(5);
      track("Assessment Results Viewed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not complete the assessment. Please try again.");
      setStepIdx(3);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.email.trim()) return toast.error("Please add your email");
    setSubmitting(true);
    try {
      await api.post(`/public/assessment/${result.assessment_id}/lead`, lead);
      setLeadDone(true);
      track("Lead Form Submitted");
      toast.success("Your assessment is on its way");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  const scheduleDemo = () => { track("Demo CTA Clicked"); onScheduleDemo?.(result.assessment_id); };

  return (
    <section id="assessment" className="py-20 sm:py-28 bg-secondary/40" data-testid="assessment-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* INTRO */}
        {stepIdx === -1 && (
          <div className="text-center" data-testid="assessment-intro">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-navy bg-gold/20 border border-gold/30 rounded-full px-4 py-1.5 mb-6">
              <Gauge className="w-3.5 h-3.5" /> 60-Second Assessment
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-navy leading-tight mb-4">{config.title}</h2>
            <p className="text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8">{config.supporting}</p>
            <Button onClick={start} className="gold-gradient text-navy font-bold h-12 px-8 gold-sheen" data-testid="assessment-start">
              Analyze My Dealership <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        )}

        {/* STEPS */}
        {stepIdx >= 0 && stepIdx <= 3 && (
          <div className="bg-white rounded-2xl border border-border p-6 sm:p-8" data-testid={`assessment-step-${stepIdx}`}>
            <div className="flex items-center gap-2 mb-6">
              {steps.map((_, i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= stepIdx ? "bg-gold" : "bg-slate-200"}`} />
              ))}
            </div>
            <p className="text-xs font-bold uppercase tracking-widest text-gold mb-1">Step {stepIdx + 1} of {steps.length}</p>
            <h3 className="text-xl font-bold text-navy mb-6">{steps[stepIdx]}</h3>

            <div className="space-y-7">
              {qForStep(stepIdx).map((q) => (
                <div key={q.id} data-testid={`question-${q.id}`}>
                  <Label className="text-navy font-semibold block mb-3">{q.label}{q.optional && <span className="text-muted-foreground font-normal text-xs"> (optional)</span>}</Label>
                  <div className={q.type === "multi" || q.options.length > 4 ? "grid sm:grid-cols-2 gap-2" : "grid grid-cols-1 gap-2"}>
                    {q.options.map((o) => {
                      const [val, lab] = o;
                      const selected = q.type === "multi" ? (answers[q.id] || []).includes(val) : answers[q.id] === val;
                      return (
                        <button key={val} type="button"
                          onClick={() => q.type === "multi" ? toggleMulti(q.id, val, q.max || 3) : setSingle(q.id, val)}
                          data-testid={`option-${q.id}-${val}`}
                          className={`text-left px-4 py-3 rounded-xl border-2 text-sm font-medium transition-colors ${selected ? "border-gold bg-gold/10 text-navy" : "border-border bg-white text-navy hover:border-navy/30"}`}>
                          <span className="flex items-center gap-2">
                            {selected && <CheckCircle2 className="w-4 h-4 text-gold shrink-0" />}{lab}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-8">
              <Button variant="ghost" onClick={back} disabled={stepIdx === 0} className="text-muted-foreground" data-testid="assessment-back">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
              </Button>
              <Button onClick={next} className="gold-gradient text-navy font-bold h-11 px-7 gold-sheen" data-testid="assessment-next">
                {stepIdx < 3 ? "Continue" : "Analyze My Dealership"} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {/* LOADING */}
        {stepIdx === 4 && (
          <div className="bg-white rounded-2xl border border-border p-12 text-center" data-testid="assessment-loading">
            <Loader2 className="w-10 h-10 text-gold animate-spin mx-auto mb-4" />
            <h3 className="text-xl font-bold text-navy">Analyzing your dealership...</h3>
            <p className="text-muted-foreground mt-1">Identifying where Marine Ad AI could have the biggest impact.</p>
          </div>
        )}

        {/* RESULTS */}
        {stepIdx === 5 && result && (
          <div className="space-y-6" data-testid="assessment-results">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-navy bg-gold/20 border border-gold/30 rounded-full px-4 py-1.5 mb-4">
                <Sparkles className="w-3.5 h-3.5" /> Your Marine Marketing Profile
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-navy">Here is where Marine Ad AI can help most</h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-3" data-testid="assessment-categories">
              {result.categories.map((c) => (
                <div key={c.key} className="bg-white rounded-xl border border-border p-4 flex items-center justify-between" data-testid={`category-${c.key}`}>
                  <span className="font-semibold text-navy text-sm">{c.label}</span>
                  <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${LEVEL_STYLES[c.level_label] || "bg-slate-100 text-slate-700"}`}>{c.level_label}</span>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-border p-6">
              <h3 className="font-bold text-navy mb-3 flex items-center gap-2"><Compass className="w-4 h-4 text-gold" /> Recommended Marine Ad AI Capabilities</h3>
              <div className="flex flex-wrap gap-2" data-testid="assessment-capabilities">
                {result.recommended_capabilities.map((cap) => (
                  <span key={cap.key} className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${cap.available ? "bg-navy text-white border-navy" : "bg-white text-navy border-dashed border-navy/40"}`} data-testid={`capability-${cap.key}`}>
                    {cap.label}{!cap.available && <span className="text-[10px] font-normal opacity-70"> · coming soon</span>}
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-border p-6" data-testid="assessment-narrative">
              <h3 className="font-bold text-navy mb-3">Your Personalized Assessment</h3>
              {result.ai_assessment.split("\n\n").filter(Boolean).map((p, i) => (
                <p key={i} className="text-sm text-slate-700 leading-relaxed mb-3">{p}</p>
              ))}
            </div>

            {/* CONVERSION */}
            <div className="bg-navy rounded-2xl p-6 sm:p-8 text-center">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Schedule My Marine Ad AI Demo</h3>
              <p className="text-slate-300 text-sm max-w-xl mx-auto mb-6">See how Marine Ad AI could work with your inventory, advertising, and lead-tracking workflow.</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Button onClick={scheduleDemo} className="gold-gradient text-navy font-bold h-12 px-7 gold-sheen" data-testid="assessment-schedule-demo">
                  Schedule My Demo <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <Button variant="outline" onClick={() => { setLeadOpen((o) => !o); track("Lead Form Opened"); }} className="h-12 px-6 border-white/30 text-white bg-white/5 hover:bg-white/15 hover:text-white font-semibold" data-testid="assessment-email-me">
                  <Mail className="w-4 h-4 mr-1.5" /> Email Me My Assessment
                </Button>
                <Button variant="ghost" onClick={() => onExplore?.()} className="h-12 px-6 text-white hover:bg-white/10 hover:text-white font-semibold" data-testid="assessment-explore">
                  Explore Features
                </Button>
              </div>

              {leadOpen && !leadDone && (
                <form onSubmit={submitLead} className="mt-6 bg-white rounded-xl p-5 text-left max-w-xl mx-auto" data-testid="assessment-lead-form">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-navy text-xs">First name</Label><Input value={lead.first_name} onChange={(e) => setLead({ ...lead, first_name: e.target.value })} data-testid="lead-first-name" /></div>
                    <div className="space-y-1"><Label className="text-navy text-xs">Last name</Label><Input value={lead.last_name} onChange={(e) => setLead({ ...lead, last_name: e.target.value })} data-testid="lead-last-name" /></div>
                    <div className="space-y-1"><Label className="text-navy text-xs">Dealership</Label><Input value={lead.dealership_name} onChange={(e) => setLead({ ...lead, dealership_name: e.target.value })} data-testid="lead-dealership" /></div>
                    <div className="space-y-1"><Label className="text-navy text-xs">Email</Label><Input type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} data-testid="lead-email" /></div>
                    <div className="space-y-1 sm:col-span-2"><Label className="text-navy text-xs">Phone (optional)</Label><Input type="tel" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} data-testid="lead-phone" /></div>
                  </div>
                  <Button type="submit" disabled={submitting} className="mt-4 w-full sm:w-auto bg-navy text-white font-bold" data-testid="lead-submit">
                    {submitting ? "Sending..." : "Send Me My Full Assessment"}
                  </Button>
                </form>
              )}
              {leadDone && <p className="mt-4 text-emerald-300 text-sm font-semibold" data-testid="lead-done">Thanks. Your full assessment is on its way.</p>}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
